import { ApiInstance, scopedTo } from '@/lib/api'
import { formatDate, plural } from '@/lib/format'
import type { OnboardingState } from '@/lib/onboarding'

export type MissionId =
  | 'site'
  | 'property'
  | 'brand'
  | 'watermark'
  | 'domain'
  | 'team'
  | 'whatsapp'
  | 'attendant'
  | 'chat'
  | 'instagram'
  | 'about'
  | 'blog-post'
  | 'studio-video'
  | 'deal'
  | 'contract'
  | 'follow-instagram'
  | 'follow-tiktok'
  | 'follow-linkedin'

export type MissionKind = 'verified' | 'follow'

export interface MissionLevel {
  level: number
  minPoints: number
  days: number
}

export interface MissionReward {
  days: number
  status: 'pending' | 'applied'
  newPeriodEnd: string | null
}

export interface MissionLevelProgress extends MissionLevel {
  reached: boolean
  reward: MissionReward | null
}

export interface Mission {
  id: MissionId
  points: number
  kind: MissionKind
  done: boolean
  completedAt: string | null
}

export interface MissionBoard {
  points: number
  maxPoints: number
  level: MissionLevel
  nextLevel: MissionLevel | null
  levels: MissionLevelProgress[]
  missions: Mission[]
  days: { earned: number; applied: number; pending: number; cap: number }
  billing: { status: string | null; kind: 'trial' | 'paid' | null; endsAt: string | null }
}

export interface MissionLevelUp {
  level: number
  days: number
  status: 'pending' | 'applied'
  newPeriodEnd: string | null
}

export interface MissionSyncResult {
  board: MissionBoard
  completed: MissionId[]
  levelUps: MissionLevelUp[]
}

export const SOCIAL_PROFILES: Partial<Record<MissionId, string>> = {
  'follow-instagram': 'https://www.instagram.com/habitar.io/',
  'follow-tiktok': 'https://www.tiktok.com/@habitar.io',
  'follow-linkedin': 'https://www.linkedin.com/company/habitar-io/'
}

const SETTINGS = (companyId: string) => `/empresas/${companyId}/configuracoes`

export const MISSION_COPY: Record<MissionId, { title: string; action: string; path?: (companyId: string) => string }> = {
  site: { title: 'Coloque seu site no ar', action: 'Personalizar site', path: id => `${SETTINGS(id)}/site/personalizacao` },
  property: { title: 'Cadastre seu primeiro imóvel', action: 'Cadastrar imóvel', path: () => '/imoveis/novo-imovel' },
  brand: { title: 'Aplique sua logo e cores', action: 'Enviar logo', path: id => `${SETTINGS(id)}/site/personalizacao` },
  watermark: { title: "Proteja as fotos com marca d'água", action: "Ativar marca d'água", path: id => `${SETTINGS(id)}/geral/marca-dagua` },
  domain: { title: 'Conecte seu domínio próprio', action: 'Conectar domínio', path: id => `${SETTINGS(id)}/site/dominio` },
  team: { title: 'Convide sua equipe', action: 'Convidar equipe', path: id => `${SETTINGS(id)}/equipe` },
  whatsapp: { title: 'Conecte o WhatsApp', action: 'Conectar WhatsApp', path: id => `${SETTINGS(id)}/integracoes/whatsapp` },
  attendant: { title: 'Ligue o atendente IA', action: 'Ligar atendente', path: id => `${SETTINGS(id)}/atendente-ia` },
  chat: { title: 'Ative o atendimento no site', action: 'Ativar atendimento', path: id => `${SETTINGS(id)}/site/chat` },
  instagram: { title: 'Conecte o Instagram', action: 'Conectar Instagram', path: id => `${SETTINGS(id)}/integracoes/instagram` },
  about: { title: 'Conte a sua história', action: 'Criar página Sobre', path: id => `${SETTINGS(id)}/site/sobre` },
  'blog-post': { title: 'Publique o primeiro post no blog', action: 'Escrever post', path: () => '/blog/novo' },
  'studio-video': { title: 'Crie seu primeiro vídeo no Studio', action: 'Criar vídeo', path: () => '/studio/novo' },
  deal: { title: 'Registre o primeiro negócio no CRM', action: 'Abrir CRM', path: () => '/crm' },
  contract: { title: 'Cadastre o primeiro contrato', action: 'Novo contrato', path: () => '/contratos/novo' },
  'follow-instagram': { title: 'Siga a Habitar no Instagram', action: 'Seguir' },
  'follow-tiktok': { title: 'Siga a Habitar no TikTok', action: 'Seguir' },
  'follow-linkedin': { title: 'Siga a Habitar no LinkedIn', action: 'Seguir' }
}

const LEVEL_LABELS = ['Iniciante', 'Promissora', 'Em ascensão', 'Conectada', 'Em destaque', 'Profissional', 'Especialista', 'Referência']

export const MISSIONS_PATH = '/missoes'

export const missionsQueryKey = (companyId: string) => ['missions', companyId]

export const missionsUnlocked = (onboarding?: OnboardingState | null): boolean => onboarding?.status === 'completed'

export const levelLabel = (level: number): string => LEVEL_LABELS[level - 1] ?? LEVEL_LABELS[LEVEL_LABELS.length - 1]

export const missionPath = (id: MissionId, companyId: string): string | null => MISSION_COPY[id].path?.(companyId) ?? null

export const daysLabel = (days: number): string => plural(days, 'dia', 'dias')

export function levelProgress(board: MissionBoard): { percent: number; toNext: number } {
  const { points, level, nextLevel } = board
  if (!nextLevel) return { percent: 100, toNext: 0 }
  const span = nextLevel.minPoints - level.minPoints
  return { percent: Math.min(100, Math.max(0, ((points - level.minPoints) / span) * 100)), toNext: Math.max(0, nextLevel.minPoints - points) }
}

export const missionsDone = (board: MissionBoard): number => board.missions.filter(mission => mission.done).length

export const missionsComplete = (board: MissionBoard): boolean => board.missions.every(mission => mission.done)

export const nextPendingMission = (board: MissionBoard): Mission | null => board.missions.find(mission => !mission.done) ?? null

export function unseenCelebration(
  companyId: string,
  result: MissionSyncResult,
  seen: ReadonlySet<string>
): { completed: MissionId[]; levelUps: MissionLevelUp[]; keys: string[] } {
  const completed = result.completed.filter(id => !seen.has(`${companyId}:mission:${id}`))
  const levelUps = result.levelUps.filter(levelUp => !seen.has(`${companyId}:level:${levelUp.level}`))
  return {
    completed,
    levelUps,
    keys: [...completed.map(id => `${companyId}:mission:${id}`), ...levelUps.map(levelUp => `${companyId}:level:${levelUp.level}`)]
  }
}

export function billingLine(billing: MissionBoard['billing']): string | null {
  if (!billing.endsAt) return null
  if (billing.kind === 'trial') return `Seu teste grátis vai até ${formatDate(billing.endsAt)}`
  if (billing.kind === 'paid') return `Próxima cobrança em ${formatDate(billing.endsAt)}`
  return null
}

export async function syncMissions(companyId: string): Promise<MissionSyncResult> {
  const { data } = await ApiInstance.post<MissionSyncResult>('/subscriptions/missions/sync', undefined, scopedTo(companyId))
  return data
}

export async function claimMission(id: MissionId, companyId: string): Promise<MissionSyncResult> {
  const { data } = await ApiInstance.post<MissionSyncResult>(`/subscriptions/missions/${id}/claim`, undefined, scopedTo(companyId))
  return data
}
